import React, { useState, useEffect, useRef } from 'react';
import { Star, ArrowRight } from 'lucide-react';
import './FeaturedModule.css';

const FeaturedModule = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }
        return () => observer.disconnect();
    }, []);

    return (
        <section id="featured-module" className="container featured-section" ref={sectionRef}>
            <div className="featured-card">

                {/* Header */}
                <div className="featured-header">
                    <span className="featured-badge">
                        <Star size={14} className="badge-icon" />
                        FEATURED MODULE
                    </span>
                    <h2 className="featured-title">OEE: Overall Equipment Effectiveness</h2>
                </div>

                {/* Main Content Split */}
                <div className="featured-content">

                    {/* Left: Core Capabilities */}
                    <div className="capabilities-section">
                        <h3 className="capabilities-title">Core Capabilities</h3>

                        <div className="capability-item">
                            <ArrowRight className="capability-icon" />
                            <div>
                                <h4 className="capability-name">Real-Time Monitoring</h4>
                                <p className="capability-desc">Live availability, performance, quality metrics</p>
                            </div>
                        </div>

                        <div className="capability-item">
                            <ArrowRight className="capability-icon" />
                            <div>
                                <h4 className="capability-name">Downtime Tracking</h4>
                                <p className="capability-desc">Automated capture with root cause categorisation</p>
                            </div>
                        </div>

                        <div className="capability-item">
                            <ArrowRight className="capability-icon" />
                            <div>
                                <h4 className="capability-name">Trend Analysis</h4>
                                <p className="capability-desc">Historical patterns and predictive insights</p>
                            </div>
                        </div>

                        <div className="capability-item">
                            <ArrowRight className="capability-icon" />
                            <div>
                                <h4 className="capability-name">Mobile Accessibility</h4>
                                <p className="capability-desc">Shop floor data entry and executive dashboards</p>
                            </div>
                        </div>
                    </div>

                    {/* Right: Dashboard Image */}
                    <div className="dashboard-preview-container">
                        <img
                            src="./assets/modules/featured.jpeg"
                            alt="OEE Analytics Dashboard Preview"
                            className="dashboard-preview-img"
                        />
                    </div>
                </div>



            </div>

            <div className="featured-card martop1">
                {/* Bottom Stats */}
                <div className="featured-stats-section">
                    <h3 className="stats-title">Real Manufacturing Impact Benchmarks</h3>

                    <div className="stats-grid">

                        {/* Stat 1 */}
                        <div className="stat-card">
                            <div className="circular-progress">
                                <svg viewBox="0 0 36 36" className="circular-chart orange">
                                    <path className="circle-bg"
                                        d="M18 2.0845
                                        a 15.9155 15.9155 0 0 1 0 31.831
                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <path className="circle"
                                        strokeDasharray={`${isVisible ? 40 : 0}, 100`}
                                        d="M18 2.0845
                                        a 15.9155 15.9155 0 0 1 0 31.831
                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <text x="18" y="20.5" className="percentage">30-50%</text>
                                </svg>
                            </div>
                            <p className="stat-desc">Predictive maintenance<br />reduces downtime</p>
                        </div>

                        {/* Stat 2 */}
                        <div className="stat-card">
                            <div className="circular-progress">
                                <svg viewBox="0 0 36 36" className="circular-chart orange">
                                    <path className="circle-bg"
                                        d="M18 2.0845
                                        a 15.9155 15.9155 0 0 1 0 31.831
                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <path className="circle"
                                        strokeDasharray={`${isVisible ? 30 : 0}, 100`}
                                        d="M18 2.0845
                                        a 15.9155 15.9155 0 0 1 0 31.831
                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <text x="18" y="20.5" className="percentage">20-40%</text>
                                </svg>
                            </div>
                            <p className="stat-desc">Extends machine life by</p>
                        </div>

                        {/* Stat 3 */}
                        <div className="stat-card">
                            <div className="circular-progress">
                                <svg viewBox="0 0 36 36" className="circular-chart orange">
                                    <path className="circle-bg"
                                        d="M18 2.0845
                                        a 15.9155 15.9155 0 0 1 0 31.831
                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <path className="circle"
                                        strokeDasharray={`${isVisible ? 25 : 0}, 100`}
                                        d="M18 2.0845
                                        a 15.9155 15.9155 0 0 1 0 31.831
                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <text x="18" y="20.5" className="percentage">10-40%</text>
                                </svg>
                            </div>
                            <p className="stat-desc">Reduces maintenance<br />cost by</p>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturedModule;
